/**
 * The map visual's state selection, as pure functions.
 *
 * Which of the four surfaces a map block resolves to is the part of this
 * component most likely to regress — it depends on the parse result, on how
 * many pins actually carry a position, and on whether WebGL is still alive. So
 * it lives here as data-in/data-out rather than as conditions buried in JSX,
 * and `map-render-model.assert.ts` pins every combination.
 *
 * Deliberately React-free and DOM-free so that assert runs under plain `tsx`.
 */

import { GeoResolveStatus } from '@cabal/shared/domain/geo/enums.js';
import type { AmbiguityCandidate } from '@cabal/shared/domain/geo/types.js';
import { MapPinKind } from '@cabal/shared/domain/map/enums.js';
import type { MapPin, ParsedMap } from '@cabal/shared/domain/map/types.js';
import { MAP_HEADER_PATTERN } from '../Cmarkdown/mermaid-visual-patterns';
import {
  MAP_ACCESSIBLE_NAME_FALLBACK,
  MAP_ACCESSIBLE_NAME_LOCATION_NOUN,
  MAP_ACCESSIBLE_NAME_LOCATIONS_NOUN,
  MAP_AMBIGUOUS_CANDIDATE_LIMIT,
  MAP_AMBIGUOUS_CANDIDATE_SEPARATOR,
  MAP_AMBIGUOUS_DETAIL_PREFIX,
  MAP_AMBIGUOUS_OVERFLOW_LABEL,
  MAP_COORDINATE_FRACTION_DIGITS,
  MAP_LOOKUP_FAILED_DETAIL_TEXT,
  MAP_NOT_FOUND_DETAIL_TEXT,
  MAP_UNRESOLVED_DETAIL_TEXT,
} from './map-constants';
import type { MapPoint } from './map-style';

/**
 * Where a block's places stand with respect to acquiring positions.
 *
 * This is an input to surface selection, not an output: a block with no
 * positions is a different product state depending on WHY it has none, and
 * collapsing those states was the bug that sent valid blocks to the error card.
 * `useMapGeocoding` supplies the live phase. Outside stored session content
 * there is no content ref to resolve against, so a named place has no channel
 * at all — which is exactly `Unavailable`, and never a failed lookup.
 */
export enum MapResolutionPhase {
  /** Resolution finished (or was never needed). Positions are final for now. */
  Settled = 'settled',
  /** A lookup pass is in flight. */
  Resolving = 'resolving',
  /** Lookups ran and returned nothing usable for the remaining places. */
  Failed = 'failed',
  /** Lookups cannot run for this block — there is no resolution channel here. */
  Unavailable = 'unavailable',
}

/** The surfaces a map block can resolve to. Exactly one renders. */
export enum MapRenderSurface {
  /**
   * The block named nothing plottable at all — parse returned null. This is the
   * ONLY case that reaches the shared error card: there is no list to show,
   * because there are no place names to list.
   */
  ParseFailed = 'parseFailed',
  /** Lookups are in flight: a skeleton where the map will be, names retained. */
  Resolving = 'resolving',
  /** The drawn map, with the location list alongside it. */
  MapWithList = 'mapWithList',
  /**
   * The location list carries the block on its own — because nothing resolved,
   * because nothing here can resolve, or because the canvas failed to draw.
   * Which of those it is comes from the accompanying `MapListNote`.
   */
  ListOnly = 'listOnly',
}

/** Why the list is standing on its own. Never a reason to hide the names. */
export enum MapListNote {
  None = 'none',
  /** Lookups finished; none of these places could be placed. */
  Unresolved = 'unresolved',
  /** There is no channel here that could place them. */
  ResolutionUnavailable = 'resolutionUnavailable',
  /** Positions exist, but the map itself could not be drawn. */
  CanvasUnavailable = 'canvasUnavailable',
}

export interface MapRenderPlan {
  surface: MapRenderSurface;
  note: MapListNote;
}

/**
 * The places a map block restates what a pin IS.
 *
 * A block draws all of them, which is right when the block is the only thing on
 * screen saying where these places are. It is wrong when the embedder's own
 * chrome already names the place directly above the map — then the same label
 * arrives three times over. So the set is addressable: an embedder names the
 * restatements its surroundings already carry, and nothing else changes.
 */
export enum MapPinRestatement {
  /** The block's own glass card rail (`MapLocationList`). A marker carries no
   *  name of its own — only its glyph, aria-label and click popup — so on a
   *  drawn surface the rail is where a pin's name lives, and on a list-only
   *  or resolving surface it is the block's ONLY text. */
  LocationRail = 'locationRail',
  /** The rounded lat/lon a positioned coordinate pin's row trails with. */
  CoordinateDetail = 'coordinateDetail',
}

/** Every restatement drawn — what a block renders when the embedder names
 *  none. A module constant so the default keeps a stable identity across
 *  renders. */
export const MAP_NO_SUPPRESSED_RESTATEMENTS: readonly MapPinRestatement[] = [];

/** The restatement decision, resolved once and read as plain booleans by the
 *  components that own each print. */
export interface MapRestatementChrome {
  showLocationRail: boolean;
  showCoordinateDetail: boolean;
}

interface MapRestatementChromeInput {
  suppressed: readonly MapPinRestatement[];
}

export function resolveMapRestatementChrome({
  suppressed,
}: MapRestatementChromeInput): MapRestatementChrome {
  return {
    showLocationRail: !suppressed.includes(MapPinRestatement.LocationRail),
    showCoordinateDetail: !suppressed.includes(MapPinRestatement.CoordinateDetail),
  };
}

/**
 * Whether a surface draws the WebGL canvas. The location list renders on every
 * surface except the parse failure, which is why only this half needs a table.
 */
export const MAP_CANVAS_VISIBLE_BY_SURFACE: Record<MapRenderSurface, boolean> = {
  [MapRenderSurface.ParseFailed]: false,
  [MapRenderSurface.Resolving]: false,
  [MapRenderSurface.MapWithList]: true,
  [MapRenderSurface.ListOnly]: false,
};

/** The list note that explains an empty result, by why it is empty. */
const EMPTY_RESULT_NOTE_BY_PHASE: Record<MapResolutionPhase, MapListNote> = {
  [MapResolutionPhase.Settled]: MapListNote.Unresolved,
  [MapResolutionPhase.Failed]: MapListNote.Unresolved,
  [MapResolutionPhase.Unavailable]: MapListNote.ResolutionUnavailable,
  // Never read — an in-flight phase resolves to the skeleton surface, which
  // carries no note. Present so the record stays exhaustive.
  [MapResolutionPhase.Resolving]: MapListNote.None,
};

/**
 * What the geocoder decided about one place name.
 *
 * The verdict travels with the position rather than being collapsed into it,
 * because "no position" is four different products: nothing came back, the name
 * matched nothing, the lookup broke, or the name matched several real places.
 * Only the last one is actionable by the reader, and only because its rivals
 * come with it.
 */
export interface MapPlaceResolution {
  status: GeoResolveStatus;
  /** Set only for a `Resolved` verdict. An ambiguous hit is deliberately
   *  position-less: presenting one of several rivals as the answer is the guess
   *  the health gate exists to refuse. */
  position: MapPoint | null;
  /** The rival interpretations, for an `Ambiguous` verdict. Empty otherwise. */
  candidates: AmbiguityCandidate[];
  /** Display name from a resolved search result, or the geocoder's name.
   *  Null when none came back. */
  name: string | null;
}

/** One row of the location list, and — when positioned — one map marker.
 *  `position` is null for a place that has not been geocoded; the geocoding
 *  hook fills it by supplying `placeResolutions`, which also carries WHY a
 *  place has no position. */
export interface MapLocationEntry {
  key: string;
  label: string | null;
  /** The place text as written, a resolved search-result title, or null for a
   *  literal-coordinate pin. */
  place: string | null;
  /** Canonical `sr#` when the pin is a search result. */
  sid: string | null;
  /** The author's prose about this place, or null when they wrote none. It is
   *  content, not status: it never substitutes for `resolution`, and nothing
   *  here may derive it from one. */
  description: string | null;
  /** The authored icon keyword, or null when the pin carries none. A
   *  display vocabulary only — `map-pin-icon.tsx` resolves it to a glyph, and
   *  it never reaches a geocoder. */
  icon: string | null;
  position: MapPoint | null;
  /** The resolver's verdict for a named place or search result, or null when
   *  none has come back — including every coordinate pin, which never needs one. */
  resolution: MapPlaceResolution | null;
}

/**
 * The text slots of one location row, already decided.
 *
 * Which text leads is the whole point of the list surface, so it is settled
 * here rather than in JSX: an unresolved row MUST lead with the place, because
 * the reader's question is "which place could you not place?" and a label like
 * "Engineering" answers nothing. A resolved row leads with its label — it has a
 * marker on the map tying it to a position, so the label is the useful handle.
 *
 * `detail` and `description` are two different kinds of text and get two
 * slots for that reason. `detail` is the system talking about the pin (where
 * it sits, or why it has nowhere to sit); `description` is the author talking
 * about the place. Folding one into the other would let a lookup failure
 * overwrite authored content, or authored content hide a lookup failure —
 * either way the reader loses the thing they needed.
 */
export interface MapLocationRowText {
  /** Leading text. Never null — every row says something. */
  primary: string;
  /** Supporting text beside the primary, or null when it would repeat it. */
  secondary: string | null;
  /** Trailing text: where the pin is, or why it has nowhere to be. */
  detail: string | null;
  /** The author's prose about the place, verbatim. Never derived, never a
   *  fallback for `detail`, and never suppressed by one. */
  description: string | null;
}

/** A positioned entry, ready to become a marker. */
export interface MapCanvasPoint {
  /** The source entry's key, so a marker and its rail card name the same
   *  place without comparing coordinates — two pins CAN share a position. */
  key: string;
  lat: number;
  lon: number;
  label: string;
  /** Shown under the label in the marker popup. Null when unwritten. */
  description: string | null;
  /** The authored icon keyword the marker's glyph resolves from. */
  icon: string | null;
}

/** Verdicts keyed by the pin's place text EXACTLY as written, which is the key
 *  the resolve route echoes back in `GeoResolveResult.query`. Coordinate pins
 *  never consult it. */
export type MapPlaceResolutions = ReadonlyMap<string, MapPlaceResolution>;

export const NO_PLACE_RESOLUTIONS: MapPlaceResolutions = new Map<
  string,
  MapPlaceResolution
>();

interface MapLocationEntriesInput {
  pins: MapPin[];
  placeResolutions: MapPlaceResolutions;
}

interface MapRenderPlanInput {
  parsed: ParsedMap | null;
  positionedPointCount: number;
  resolutionPhase: MapResolutionPhase;
  isCanvasAvailable: boolean;
}

interface MapAccessibleNameInput {
  title: string | null;
  positionedPointCount: number;
}

/**
 * A mermaid-fenced map carries its `map` header inside the block body, and a
 * model may repeat the header inside a ```map fence too. Left in place the
 * parser reads it as a place named "map" and plots a phantom pin, so it is
 * stripped once, here, for both fence paths.
 */
export function stripMapFenceHeader(content: string): string {
  return content.replace(MAP_HEADER_PATTERN, '');
}

function entryForPin({
  pin,
  index,
  placeResolutions,
}: {
  pin: MapPin;
  index: number;
  placeResolutions: MapPlaceResolutions;
}): MapLocationEntry {
  const key = `map-pin-${index}`;

  if (pin.kind === MapPinKind.Coordinates) {
    return {
      key,
      label: pin.label,
      place: null,
      sid: null,
      description: pin.description,
      icon: pin.icon,
      position: { lat: pin.lat, lon: pin.lon },
      resolution: null,
    };
  }

  if (pin.kind === MapPinKind.SearchResult) {
    const resolution = placeResolutions.get(pin.sid) ?? null;
    return {
      key,
      label: pin.label,
      place: resolution?.name ?? null,
      sid: pin.sid,
      description: pin.description,
      icon: pin.icon,
      position: resolution?.position ?? null,
      resolution,
    };
  }

  const resolution = placeResolutions.get(pin.place) ?? null;
  return {
    key,
    label: pin.label,
    place: pin.place,
    sid: null,
    description: pin.description,
    icon: pin.icon,
    position: resolution?.position ?? null,
    resolution,
  };
}

export function buildMapLocationEntries({
  pins,
  placeResolutions,
}: MapLocationEntriesInput): MapLocationEntry[] {
  return pins.map((pin, index) => entryForPin({ pin, index, placeResolutions }));
}

/** Whether this block has anything the resolve route could look up. A block of
 *  literal coordinates needs no request at all, and issuing one would spend the
 *  deployment's shared rate budget on nothing. */
export function hasResolvablePlaces(parsed: ParsedMap | null): boolean {
  return (parsed?.pins ?? []).some(
    (pin) => pin.kind === MapPinKind.Place || pin.kind === MapPinKind.SearchResult,
  );
}

function appendCaption({
  placeName,
  label,
}: {
  placeName: string | null;
  label: string | null;
}): string | null {
  if (placeName && label && placeName !== label) {
    return `${placeName} ${label}`;
  }
  return placeName ?? label;
}

/** The name a marker prints — the place, with the caption label appended. */
export function mapPinMapName(entry: MapLocationEntry): string {
  const composed = appendCaption({ placeName: entry.place, label: entry.label });
  if (composed) {
    return composed;
  }
  if (entry.position) {
    return formatMapCoordinate(entry.position);
  }
  return entry.sid ?? '';
}

export function mapCanvasPointsFromEntries(
  entries: MapLocationEntry[],
): MapCanvasPoint[] {
  return entries.flatMap((entry) =>
    entry.position
      ? [
          {
            key: entry.key,
            lat: entry.position.lat,
            lon: entry.position.lon,
            label: mapPinMapName(entry),
            description: entry.description,
            icon: entry.icon,
          },
        ]
      : [],
  );
}

export function formatMapCoordinate(position: MapPoint): string {
  return `${position.lat.toFixed(MAP_COORDINATE_FRACTION_DIGITS)}, ${position.lon.toFixed(
    MAP_COORDINATE_FRACTION_DIGITS,
  )}`;
}

/** How one rival interpretation reads in a row. Enough to tell it apart from
 *  its rivals — `Springfield, Illinois` — never the vendor's full display
 *  string, which is a paragraph. */
function ambiguityCandidateText(candidate: AmbiguityCandidate): string {
  const qualifier = candidate.region ?? candidate.country;
  return qualifier ? `${candidate.name}, ${qualifier}` : candidate.name;
}

/** The trailing text of a row with no position, by why it has none. A verdict
 *  that arrived with no usable status still reads as the neutral default —
 *  never as a failure the block did not have. */
const UNPOSITIONED_DETAIL_TEXT_BY_STATUS: Record<GeoResolveStatus, string> = {
  [GeoResolveStatus.Resolved]: MAP_UNRESOLVED_DETAIL_TEXT,
  [GeoResolveStatus.Ambiguous]: MAP_AMBIGUOUS_DETAIL_PREFIX,
  [GeoResolveStatus.NotFound]: MAP_NOT_FOUND_DETAIL_TEXT,
  [GeoResolveStatus.Failed]: MAP_LOOKUP_FAILED_DETAIL_TEXT,
};

/**
 * Why this row has no marker, in the reader's terms.
 *
 * An ambiguous name is the one case that hands the reader something to do: the
 * geocoder found several real places and refused to guess, so the row names the
 * rivals. Without them "Ambiguous" is a shrug, and the reader cannot tell that
 * writing `Springfield, Illinois` would fix it.
 */
export function mapUnpositionedDetailText(resolution: MapPlaceResolution | null): string {
  if (!resolution) {
    return MAP_UNRESOLVED_DETAIL_TEXT;
  }

  if (
    resolution.status !== GeoResolveStatus.Ambiguous ||
    resolution.candidates.length === 0
  ) {
    return UNPOSITIONED_DETAIL_TEXT_BY_STATUS[resolution.status];
  }

  const shown = resolution.candidates
    .slice(0, MAP_AMBIGUOUS_CANDIDATE_LIMIT)
    .map(ambiguityCandidateText);
  const hiddenCount = resolution.candidates.length - shown.length;
  const listed = (
    hiddenCount > 0 ? [...shown, `+${hiddenCount} ${MAP_AMBIGUOUS_OVERFLOW_LABEL}`] : shown
  ).join(MAP_AMBIGUOUS_CANDIDATE_SEPARATOR);

  return `${MAP_AMBIGUOUS_DETAIL_PREFIX}: ${listed}`;
}

/** An ambiguous row's detail is a list of place names, not a two-word status,
 *  so the list gives it room to wrap. */
export function isAmbiguousEntry(entry: MapLocationEntry): boolean {
  return entry.resolution?.status === GeoResolveStatus.Ambiguous;
}

/**
 * What one row of the location list says.
 *
 * There is exactly one path to the "where is it" text — `place` if the author
 * named one, otherwise the formatted coordinates — used for both the resolved
 * row's trailing text and, by leading with it, the unresolved row's identity.
 * A slot is null rather than a repeat: when place and label are the same string
 * (the common case, since a label defaults to its place) the row renders one
 * line, not the same words twice.
 *
 * The description is passed straight through on every branch. It is the
 * author's text, so no resolution outcome may edit, replace or silence it.
 */
export function mapLocationRowText(entry: MapLocationEntry): MapLocationRowText {
  const placeText = entry.place ?? (entry.position ? formatMapCoordinate(entry.position) : null);

  if (!entry.position) {
    const identity = placeText ?? entry.sid;
    const primary = identity ?? entry.label ?? '';
    return {
      primary,
      secondary: entry.label && entry.label !== primary ? entry.label : null,
      detail: mapUnpositionedDetailText(entry.resolution),
      description: entry.description,
    };
  }

  if (entry.place === null) {
    const coordText = formatMapCoordinate(entry.position);
    const primary = entry.label ?? coordText;
    return {
      primary,
      secondary: null,
      detail: primary === coordText ? null : coordText,
      description: entry.description,
    };
  }

  return {
    primary: appendCaption({ placeName: entry.place, label: entry.label }) ?? entry.place,
    secondary: null,
    detail: null,
    description: entry.description,
  };
}

/**
 * Whether this row's trailing text is the pin's own coordinates and nothing
 * else — the branch above that fills `detail` from `formatMapCoordinate`.
 *
 * It is the only detail that restates a position the embedder may already be
 * printing itself. A named place's detail is the place, and an unpositioned
 * row's detail is the geocoder's verdict; neither repeats anything, so neither
 * is ever suppressible.
 */
export function isCoordinateDetailRow(entry: MapLocationEntry): boolean {
  return entry.place === null && entry.sid === null && entry.position !== null;
}

/**
 * The whole surface contract, in one place.
 *
 * The ordering is the product decision. A block that parsed is never thrown
 * away: whatever else is true, its place names reach the reader, because a
 * named place the reader recognizes is actionable and a blanket error card is
 * not. Only a block that named nothing plottable has nothing to show.
 *
 * Note also that partial success draws: one positioned pin among four pending
 * ones still gets a map, so the view never regresses as lookups land.
 */
export function selectMapRenderPlan({
  parsed,
  positionedPointCount,
  resolutionPhase,
  isCanvasAvailable,
}: MapRenderPlanInput): MapRenderPlan {
  if (!parsed) {
    return { surface: MapRenderSurface.ParseFailed, note: MapListNote.None };
  }

  if (positionedPointCount > 0) {
    return isCanvasAvailable
      ? { surface: MapRenderSurface.MapWithList, note: MapListNote.None }
      : { surface: MapRenderSurface.ListOnly, note: MapListNote.CanvasUnavailable };
  }

  if (resolutionPhase === MapResolutionPhase.Resolving) {
    return { surface: MapRenderSurface.Resolving, note: MapListNote.None };
  }

  return {
    surface: MapRenderSurface.ListOnly,
    note: EMPTY_RESULT_NOTE_BY_PHASE[resolutionPhase],
  };
}

/** The map canvas is a WebGL surface with no intrinsic text, so it carries its
 *  name explicitly for assistive technology. */
export function mapAccessibleName({
  title,
  positionedPointCount,
}: MapAccessibleNameInput): string {
  const named = title?.trim();
  const subject = named && named.length > 0 ? named : MAP_ACCESSIBLE_NAME_FALLBACK;
  return `${subject}, ${positionedPointCount} plotted ${
    positionedPointCount === 1 ? MAP_ACCESSIBLE_NAME_LOCATION_NOUN : MAP_ACCESSIBLE_NAME_LOCATIONS_NOUN
  }`;
}
